// intakeClient — PUBLIC (no-auth) client for the applicant-facing intake form.
//
// Unlike lib/api.ts, this NEVER attaches a Bearer token: the intake endpoints
// (/api/intake/:token) are public and gated solely by the token in the path.
// An applicant filling this form is a tenant with no login, so there is no
// Keycloak session to draw from. Mirrors the wire shapes in
// apps/api/src/routes/leaseIntakeForm.ts.

import type { AnimalAgeUnit, OtherAnimalKind } from './animalQuestions';

const API_BASE =
  import.meta.env.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

export type IntakeRole = 'primary' | 'delegate';
/** WEL-46: 'cancelled' ⇒ staff cancelled this section's request (no action needed). */
export type SectionStatus = 'not_requested' | 'requested' | 'completed' | 'cancelled';
export type AnimalType = 'dog' | 'cat' | 'other';

export interface IntakeAnimalView {
  id: string;
  name?: string;
  type?: AnimalType;
  /** WEL-173: what an 'other' animal is (absent on pre-WEL-173 animals). */
  otherKind?: OtherAnimalKind;
  otherDescription?: string;
  breed?: string;
  color?: string;
  weightLbs?: number;
  age?: number;
  ageUnit?: AnimalAgeUnit;
  tankGallons?: number;
  gender?: 'male' | 'female';
  spayedNeutered?: boolean;
  licenseNumber?: string;
  licenseMissingAck?: boolean;
  sourceApplicantId?: string;
  sourceApplicantName?: string;
  assignedToApplicantId?: string;
  authoredBy?: 'primary' | 'delegate' | 'staff';
  duplicateOfId?: string | null;
  removed?: boolean;
}

export interface IntakeDesignationData {
  designeeName?: string;
  relationship?: string;
  phone?: string;
  email?: string;
  /** Designee physical MAILING ADDRESS (WEL-56). */
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  filledBy?: 'tenant' | 'staff';
}

export interface IntakeYardCareData {
  arrangement?: 'resident_maintains' | 'lessor_arranges';
}

export interface SuspectPair {
  key: string;
  a: { name: string; type: string; weight: string; age: string; applicant: string };
  b: { name: string; type: string; weight: string; age: string; applicant: string };
}

export interface IntakeApplicantSummary {
  id: string;
  name: string;
  email: string | null;
  isPrimary: boolean;
}

export interface IntakeFormView {
  /** 'sorted' (WEL-22): a duplicate designation request the primary already settled. */
  state: 'fillable' | 'submitted' | 'unavailable' | 'sorted';
  role: IntakeRole;
  applicant: { id: string; name: string };
  propertyAddress: string | null;
  sections: {
    designation: { visible: boolean; status: SectionStatus; data: IntakeDesignationData };
    yardCare: {
      visible: boolean;
      applies: boolean;
      status: SectionStatus;
      data: IntakeYardCareData;
    };
    animals: { visible: boolean; status: SectionStatus; list: IntakeAnimalView[] };
  };
  suspects: SuspectPair[];
  delegatableApplicants: IntakeApplicantSummary[];
  /** Omitted while LEASE_KICKOFF_ENABLED is off (legacy wire stays unchanged). */
  leaseHandoff?: {
    state: 'none' | 'preparing' | 'ready' | 'emailed';
    signUrl?: string;
    leadFirstName?: string;
  };
}

export interface AnimalInput {
  id: string;
  name?: string;
  type?: AnimalType;
  otherKind?: OtherAnimalKind;
  otherDescription?: string;
  breed?: string;
  color?: string;
  weightLbs?: number;
  age?: number;
  ageUnit?: AnimalAgeUnit;
  tankGallons?: number;
  gender?: 'male' | 'female';
  spayedNeutered?: boolean;
  licenseNumber?: string;
  duplicateOfId?: string | null;
  removed?: boolean;
}

export interface IntakeSubmission {
  animals?: AnimalInput[];
  designation?: {
    designeeName?: string;
    relationship?: string;
    phone?: string;
    email?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  yardCare?: { arrangement?: 'resident_maintains' | 'lessor_arranges' };
  delegations?: Array<{
    delegateId: string;
    animalIds?: string[];
    assignDesignation?: boolean;
  }>;
  /** WEL-173: set when `animals` was built from the question matrix. */
  questionsVersion?: number;
}

export interface IntakeSubmissionResult {
  ok: true;
  view: IntakeFormView;
}

export class IntakeApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`Intake API error ${status}`);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* empty */
    }
    throw new IntakeApiError(res.status, body);
  }
  return (await res.json()) as T;
}

/** GET the scoped intake view for a token. */
export function getIntake(token: string): Promise<IntakeFormView> {
  return request<IntakeFormView>(`/api/intake/${encodeURIComponent(token)}`);
}

/** POST a submission; returns the refreshed view. */
export function submitIntake(
  token: string,
  body: IntakeSubmission,
): Promise<IntakeSubmissionResult> {
  return request<IntakeSubmissionResult>(`/api/intake/${encodeURIComponent(token)}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
